import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { FlowEdgeDef, FlowNodeDef, UseCaseFlowDef } from "./types";
import { iconForKind, kindAccent, kindSurface } from "./kindColors";
import { getInternalSteps } from "./internalSteps";
import NodeExpandCallout from "./NodeExpandCallout";

const dashMove = keyframes`
  to { stroke-dashoffset: -18; }
`;

const CELL_W = 156;
const CELL_H = 96;
const PAD_X = 20;
const PAD_Y = 16;
/** Extra vertical space under the active row while the expand callout is open. */
const CALLOUT_EXTRA = 168;

type Props = {
  flow: UseCaseFlowDef;
  pathIndex: number;
  /** Index into internal mini-steps for the active node. */
  subStepIndex: number;
  onSelectNode?: (nodeId: string) => void;
};

export default function AnimatedFlowCanvas({
  flow,
  pathIndex,
  subStepIndex,
  onSelectNode,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();

  const activeId = flow.path[pathIndex] ?? flow.path[0];
  const reached = useMemo(() => new Set(flow.path.slice(0, pathIndex + 1)), [flow.path, pathIndex]);

  const { maxRow, maxLane, byId } = useMemo(() => {
    let mr = 0;
    let ml = 0;
    const map = new Map<string, FlowNodeDef>();
    for (const n of flow.nodes) {
      map.set(n.id, n);
      mr = Math.max(mr, n.row);
      ml = Math.max(ml, n.lane);
    }
    return { maxRow: mr, maxLane: ml, byId: map };
  }, [flow.nodes]);

  const activeNode = byId.get(activeId);
  const activeRow = activeNode?.row ?? -1;
  const calloutOpen = Boolean(activeNode);

  const rowY = (row: number) =>
    PAD_Y + row * CELL_H + (calloutOpen && activeRow >= 0 && row > activeRow ? CALLOUT_EXTRA : 0);

  const width = PAD_X * 2 + (maxLane + 1) * CELL_W;
  const height =
    PAD_Y * 2 + (maxRow + 1) * CELL_H + (calloutOpen ? CALLOUT_EXTRA : 0);

  const centerOf = (node: FlowNodeDef) => ({
    x: PAD_X + node.lane * CELL_W + CELL_W / 2,
    y: rowY(node.row) + CELL_H / 2,
  });

  const edgeActive = (edge: FlowEdgeDef) => {
    if (reached.has(edge.from) && reached.has(edge.to)) return true;
    if (edge.from === activeId) return true;
    return edge.to === activeId && reached.has(edge.from);
  };

  const pathIndexOf = (nodeId: string) => flow.path.indexOf(nodeId);

  const internalSteps = useMemo(
    () => (activeNode ? getInternalSteps(flow, activeNode, t) : []),
    [activeNode, flow, t]
  );

  const summary = activeNode
    ? t(`docs.useCaseFlows.details.${flow.id}.${activeNode.id}`, {
        defaultValue: t(`docs.useCaseFlows.details.${flow.id}.fallback`),
      })
    : "";

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "auto",
        height: "100%",
        minHeight: { xs: 320, md: 420 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: (th) =>
          th.palette.mode === "dark"
            ? alpha(th.palette.common.white, 0.03)
            : alpha(th.palette.grey[500], 0.04),
      }}
    >
      <Box
        sx={{
          position: "relative",
          width,
          height,
          minWidth: "100%",
          transition: "height 0.35s ease",
        }}
      >
        <svg
          width={width}
          height={height}
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          aria-hidden
        >
          {flow.edges.map((edge) => {
            const from = byId.get(edge.from);
            const to = byId.get(edge.to);
            if (!from || !to) return null;
            const a = centerOf(from);
            const b = centerOf(to);
            const active = edgeActive(edge);
            const isAlt = edge.variant === "alt";
            const accent = kindAccent(theme, to.kind);
            const stroke = active
              ? accent
              : alpha(theme.palette.text.primary, isAlt ? 0.16 : 0.24);
            const midX = (a.x + b.x) / 2;
            const midY = (a.y + b.y) / 2;
            const path = `M ${a.x} ${a.y + 26} C ${a.x} ${midY}, ${b.x} ${midY}, ${b.x} ${b.y - 26}`;
            return (
              <g key={`${edge.from}->${edge.to}`}>
                <path
                  d={path}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={active ? 2 : isAlt ? 1.15 : 1.5}
                  strokeDasharray={isAlt ? "4 4" : active ? "6 4" : undefined}
                  style={
                    active
                      ? { animation: `${dashMove} 0.9s linear infinite` }
                      : undefined
                  }
                />
                {active && (
                  <circle r="3" fill={accent}>
                    <animateMotion dur="1.4s" repeatCount="indefinite" path={path} />
                  </circle>
                )}
                {edge.labelKey && (
                  <text
                    x={midX}
                    y={midY - 4}
                    textAnchor="middle"
                    fill={
                      active ? theme.palette.text.primary : theme.palette.text.secondary
                    }
                    fontSize="9.5"
                    fontWeight={active ? 700 : 500}
                  >
                    {t(`docs.useCaseFlows.edges.${edge.labelKey}`)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {flow.nodes.map((node) => {
          const isActive = node.id === activeId;
          const isDone = reached.has(node.id) && !isActive;
          const onPath = flow.path.includes(node.id);
          const accent = kindAccent(theme, node.kind);
          const left = PAD_X + node.lane * CELL_W + 8;
          const top = rowY(node.row) + 10;
          const stepIdx = pathIndexOf(node.id);

          return (
            <Box
              key={node.id}
              component={onPath && onSelectNode ? "button" : "div"}
              type={onPath && onSelectNode ? "button" : undefined}
              onClick={
                onPath && onSelectNode && stepIdx >= 0
                  ? () => onSelectNode(node.id)
                  : undefined
              }
              sx={{
                position: "absolute",
                left,
                top,
                width: CELL_W - 16,
                px: 1,
                py: 1,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: isActive
                  ? accent
                  : isDone
                    ? alpha(accent, 0.45)
                    : "divider",
                bgcolor: kindSurface(theme, node.kind, isActive || isDone),
                opacity: onPath || isActive || isDone ? 1 : 0.55,
                boxShadow: isActive
                  ? `0 1px 0 ${alpha(accent, 0.2)}, 0 4px 12px ${alpha(accent, 0.12)}`
                  : "none",
                transition:
                  "top 0.35s ease, border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
                textAlign: "center",
                zIndex: isActive ? 3 : 1,
                cursor: onPath && onSelectNode ? "pointer" : "default",
                font: "inherit",
                color: "inherit",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: 20,
                  color: isActive || isDone ? accent : "text.secondary",
                  display: "block",
                  mx: "auto",
                  mb: 0.25,
                }}
              >
                {iconForKind(node.kind)}
              </Icon>
              <Typography
                variant="caption"
                fontWeight={isActive ? 700 : 600}
                sx={{
                  display: "block",
                  lineHeight: 1.3,
                  fontSize: 11,
                  color: isActive || isDone ? "text.primary" : "text.secondary",
                }}
              >
                {t(`docs.useCaseFlows.nodes.${node.labelKey}`)}
              </Typography>
              {node.showRouter && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.35,
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: accent,
                  }}
                >
                  {t("docs.useCaseFlows.router.badge")}
                </Typography>
              )}
            </Box>
          );
        })}

        {activeNode && (
          <NodeExpandCallout
            key={activeNode.id}
            node={activeNode}
            left={Math.max(
              8,
              Math.min(
                PAD_X + activeNode.lane * CELL_W + 8,
                width - 300
              )
            )}
            top={rowY(activeNode.row) + CELL_H - 6}
            width={Math.min(320, Math.max(CELL_W + 40, width - 40))}
            steps={internalSteps}
            subStepIndex={subStepIndex}
            summary={summary}
          />
        )}
      </Box>
    </Box>
  );
}
